import { useFormTableCellDirtyContext } from "@/contexts/FormTableCellDirtyContext";
import { isValueDifferent } from "@/lib/utils/isValueDifferent";
import { Pencil } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";

export const QueryTableDirtyRowsStatus = () => {
  const { t } = useTranslation();
  const ctx = useFormTableCellDirtyContext();

  const recordKey = ctx?.recordKey ?? "entries";

  const currentRecords = useWatch({
    name: recordKey,
    disabled: ctx == null,
  }) as Record<string, Record<string, unknown>> | undefined;

  const count = useMemo(() => {
    if (!ctx) return 0;

    const baselineRows =
      (ctx.baseline as Record<string, Record<string, Record<string, unknown>>>)[
        recordKey
      ] ?? {};
    return countRowsDifferingBetweenSnapshots(
      baselineRows,
      currentRecords ?? {},
    );
  }, [ctx, recordKey, currentRecords]);

  if (count === 0) {
    return null;
  }

  return (
    <span className="flex items-center gap-2 shrink-0">
      <Pencil className="w-4 h-4" />
      <span>{t("table.dirtyRowsCount", { count })}</span>
    </span>
  );
};

const rowDiffersFromBaseline = (
  baselineRow: Record<string, unknown>,
  currentRow: Record<string, unknown>,
): boolean => {
  for (const k of new Set([
    ...Object.keys(baselineRow),
    ...Object.keys(currentRow),
  ])) {
    if (isValueDifferent(baselineRow[k], currentRow[k])) return true;
  }
  return false;
};

const countRowsDifferingBetweenSnapshots = (
  baselineRows: Record<string, Record<string, unknown>>,
  currentRows: Record<string, Record<string, unknown>>,
): number => {
  const ids = new Set([
    ...Object.keys(baselineRows),
    ...Object.keys(currentRows),
  ]);
  let n = 0;
  for (const id of ids) {
    if (rowDiffersFromBaseline(baselineRows[id] ?? {}, currentRows[id] ?? {})) {
      n++;
    }
  }
  return n;
};
